import React, { useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ArrowLeftIcon, IconButton, Select, useResizeObserver } from "@activecollab/components";
import PropTypes from "prop-types";
import { getConfigOption } from "~/store/selectors/selector.configOptions.jsx";
import { getAllTaskListsByProjectId } from "~/store/selectors/selector.taskLists";
import { getOpenTasksByProjectId } from "~/store/selectors/selector.tasks.jsx";
import { sortCompare } from "~/utils/utils";

export const TaskList = ({
  projectId,
  onChange,
  selectedId,
  onHeightChange,
  onBack,
  style,
  changeHeight,
  focusInput,
}) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const dimensions = useResizeObserver(ref);
  const height = dimensions?.height;

  useEffect(() => {
    if (changeHeight && height && height > 0) {
      onHeightChange?.(height);
    }
  }, [height, onHeightChange, changeHeight]);

  const showTaskNumber = useSelector((state) => getConfigOption(state, `show_task_id`));
  const tasks = useSelector((state) => getOpenTasksByProjectId(state, projectId));
  const taskLists = useSelector((state) => getAllTaskListsByProjectId(state, projectId));

  const groupedTasks = useMemo(() => {
    return taskLists.reduce((options, taskList) => {
      const taskOptions = tasks
        ?.filter((task) => task.get("task_list_id") === taskList.get("id"))
        .sort((a, b) => sortCompare(a.get("position"), b.get("position")))
        .reduce((tcc, task) => {
          tcc.push({
            id: task.get("id"),
            name: showTaskNumber ? `#${task.get("task_number")}: ${task.get("name")}` : task.get("name"),
          });

          return tcc;
        }, []);

      if (taskOptions?.length > 0) {
        options.push({
          id: `task_list_${taskList.get("id")}`,
          name: taskList.get("name"),
          options: taskOptions,
        });
      }

      return options ?? [];
    }, []);
  }, [showTaskNumber, taskLists, tasks]);

  return (
    <div
      ref={ref}
      style={style}
    >
      <Select
        options={groupedTasks}
        focusInput={focusInput}
        onChange={onChange}
        selected={selectedId > 0 ? selectedId : undefined}
        noResultText={t("No Result")}
        placeholder={t("Search a task")}
        defaultValue={t("Directly on project")}
        startAdornment={
          onBack ? (
            <IconButton
              onClick={onBack}
              variant="text gray"
              type="button"
            >
              <ArrowLeftIcon />
            </IconButton>
          ) : null
        }
        keepSameOptionsOrder
        disabledInternalSort
      />
    </div>
  );
};

TaskList.propTypes = {
  projectId: PropTypes.number,
  selectedId: PropTypes.number,
  onChange: PropTypes.func,
  onBack: PropTypes.func,
  onHeightChange: PropTypes.func,
  style: PropTypes.object,
  changeHeight: PropTypes.bool,
  focusInput: PropTypes.bool,
};

TaskList.displayName = "TaskList";
